import { formatDate } from '@angular/common';
import { CellClickedEvent } from 'ag-grid-community';
import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-enterprise';

import { formatCurrency } from '@app/shared/utils/number.utils';
import { AppIntegrationAccountProductMappingCheckboxCellRendererComponent } from 'v2/modules/app-integration-account/components/app-integration-account-product-mapping-checkbox-cell-renderer/app-integration-account-product-mapping-checkbox-cell-renderer.component';

import {
  ChannelProductMappingActionCellRendererV2Component,
  ChannelProductMappingCheckboxCellRendererV2Component,
  ChannelProductMappingSearchInvLocationComponent,
  ChannelProductMappingSearchSkuCellEditorComponent,
  ChannelProductMappingSearchUomEditorComponent,
  ChannelProductMappingSkuCellRendererComponent,
  ChannelProductMappingLastPushedCellRendererComponent,
} from '../components';
import {
  EChannelPushStockOMSProduct,
  IChannelProductMappingV2,
} from '../interfaces';

import { IChannel } from './../../shared/interfaces/mtchannel.interface';
import { DecimalPlacesPipe } from './../../shared/pipes/decimal-places.pipe';
import { EProductMappingType } from './../interfaces/channel-product-mapping.interface';
const decimalPlacesPipe = new DecimalPlacesPipe();

export interface ChannelProductAgGridActionsCellParams {
  createSku(data :IChannelProductMappingV2);
  addVariantSku(data :IChannelProductMappingV2);
  addAlternateSku(data :IChannelProductMappingV2);
  pushSingleProduct(data :IChannelProductMappingV2);
  toggleOverBuffer(data :IChannelProductMappingV2, toggled :boolean);
}
export interface ChannelProductAgGridColDefsBuildParams {
  actionsCellParams :ChannelProductAgGridActionsCellParams;
}

const _flexWidth = (minWidth :number) :Partial<ColDef> => ({
  flex: Math.floor(minWidth / 10),
  minWidth,
});

const buildProductMappingAgGridColDefs = ({
  buildParams,
  channel,
} :{
  buildParams ?:ChannelProductAgGridColDefsBuildParams;
  channel ?:IChannel | null;
}) :{
  channel :ColDef[];
  appIntegrationAccount :ColDef[];
} => {
  return {
    channel: [
      { headerCheckboxSelection: true, checkboxSelection: true, width: 40 },
      {
        headerName: 'Channel SKU',
        field: EChannelPushStockOMSProduct.SKU,
        ..._flexWidth(120),
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        sortable: true,
      },
      {
        headerName: 'Channel Product Name',
        field: EChannelPushStockOMSProduct.PRODUCT_NAME,
        ..._flexWidth(160),
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        sortable: true,
        filterParams: {
          debounceMs: 1000,
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
      },
      {
        headerName: 'Stock Lvl [Pulled]',
        field: EChannelPushStockOMSProduct.STOCK_LEVEL_PULLED,
        ..._flexWidth(160),
        sortable: false,
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 0, label: 'Out of Stock' },
            { id: 1, label: 'Has Stock' },
            { id: 100, label: 'Stocks lesser than 100' },
            { id: 500, label: 'Stocks lesser than 500' },
            { id: 1000, label: 'Stocks lesser than 1000' },
            { id: 3000, label: 'Stocks lesser than 3000' },
            { id: 3001, label: 'Stocks greater than 3000' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return `
          <div>
              ${params.data?.currentInventory || 0}
              ${
                params.data?.updatedAt
                  ? `<span class="text-muted"> [${formatDate(
                      params.data?.updatedAt,
                      'short',
                      'en-US',
                    )}]</span>`
                  : ''
              }
          </div>`;
        },
      },
      {
        headerName: 'Price',
        field: EChannelPushStockOMSProduct.PRICE,
        ..._flexWidth(100),
        sortable: true,
        cellClass: 'text-right',
        valueFormatter: ({ value }) => formatCurrency(value),
      },
      {
        hide: true,
        headerName: 'Channel',
        field: EChannelPushStockOMSProduct.CHANNEL,
        ..._flexWidth(150),
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          filterOptions: ['contains'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        sortable: false,
        cellRenderer: (params :ICellRendererParams) => {
          return params.data?.channelLabel || '';
        },
        valueGetter: (params :ValueGetterParams) => {
          return params.data?.channelLabel || '';
        },
      },
      {
        headerName: 'LUM SKU',
        field: EChannelPushStockOMSProduct.LUM_SKU,
        ..._flexWidth(180),
        cellEditorFramework: ChannelProductMappingSearchSkuCellEditorComponent,
        cellEditorParams: {
          mappingType: EProductMappingType.CHANNEL,
        },
        singleClickEdit: true,
        editable: true,
        sortable: false,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          filterOptions: ['contains', 'blank', 'notBlank'],
          defaultOption: 'contains',
          maxNumConditions: 1,
        },
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        cellRenderer: ChannelProductMappingSkuCellRendererComponent,
        cellRendererParams: {
          mappingType: EProductMappingType.CHANNEL,
        },
        valueGetter: (params) => {
          const data = params.data; // Use params.data instead of params.value

          if (!data) {
            return null;
          }
          if (data?.productOverride?.lumSku) {
            params.node.setDataValue(
              EChannelPushStockOMSProduct.SELLING_UNIT,
              data?.productOverride?.lumSku?.defaultSellingUnit?.name || '',
            );

            return `[${data.productOverride.lumSku.internalSku}] ${data.productOverride.lumSku.productName}`;
          } else if (data?.lumSku) {
            params.node.setDataValue(
              EChannelPushStockOMSProduct.SELLING_UNIT,
              data?.lumSku?.defaultSellingUnit?.name || '',
            );

            return `[${data.lumSku.internalSku}] ${data.lumSku.productName}`;
          }
        },
      },
      {
        headerName: 'Selling Unit',
        field: EChannelPushStockOMSProduct.SELLING_UNIT,
        ..._flexWidth(100),
        cellEditorFramework: ChannelProductMappingSearchUomEditorComponent,
        singleClickEdit: true,
        editable: true,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value && params.node?.rowIndex) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        cellRenderer: (params :any) => {
          if (typeof params?.value === 'object') {
            return params?.value?.name || '';
          }

          let sellingUnit = params.data?.lumSku?.defaultSellingUnit || null;
          if (
            params.data?.productOverride?.sellingUnit ||
            params.data?.productOverride?.lumSku
          ) {
            sellingUnit =
              params.data?.productOverride?.sellingUnit ||
              params.data?.productOverride?.lumSku?.defaultSellingUnit ||
              null;
          }

          if (sellingUnit) {
            return sellingUnit?.name || '';
          }

          return params?.value || '';
        },

        valueGetter: (params :ValueGetterParams) => {
          let sellingUnit = params.data?.lumSku?.defaultSellingUnit || null;
          if (
            params.data?.productOverride?.sellingUnit ||
            params.data?.productOverride?.lumSku
          ) {
            sellingUnit =
              params.data?.productOverride?.sellingUnit ||
              params.data?.productOverride?.lumSku?.defaultSellingUnit ||
              null;
          }

          if (sellingUnit) {
            return sellingUnit?.name || '';
          }

          return '';
        },
      },

      {
        headerName: 'Qty Available',
        field: EChannelPushStockOMSProduct.QTY_AVAILABLE,
        ..._flexWidth(80),
        sortable: false,
        cellRenderer: (params :ICellRendererParams) => {
          if (params.data?.productOverride?.lumSku) {
            console.log({ lumsku: params.data?.productOverride?.lumSku });
            const { channelLocationStocks } = params.data?.productOverride?.lumSku;
            const qtyAvailable =
              channelLocationStocks && channelLocationStocks.length > 0
                ? decimalPlacesPipe.transform(
                    channelLocationStocks.reduce((sum, stock) => {
                      const qtyOnHand = Number(stock.qty_onhand) || 0;
                      const qtyPending = Number(stock.qty_pending) || 0;

                      return sum + (qtyOnHand - qtyPending);
                    }, 0),
                  )
                : '';

            return qtyAvailable;
          } else if (params.data?.lumSku) {
            const { channelLocationStocks } = params.data?.lumSku;
            const qtyAvailable =
              channelLocationStocks && channelLocationStocks.length > 0
                ? decimalPlacesPipe.transform(
                    channelLocationStocks.reduce((sum, stock) => {
                      const qtyOnHand = Number(stock.qty_onhand) || 0;
                      const qtyPending = Number(stock.qty_pending) || 0;

                      return sum + (qtyOnHand - qtyPending);
                    }, 0),
                  )
                : '';

            return qtyAvailable;
          }
        },
      },
      {
        headerName: 'Buffer Quantity',
        field: EChannelPushStockOMSProduct.BUFFER_QUANTITY,
        sortable: true,
        ..._flexWidth(80),
        editable: true,
        cellEditor: 'NumericEditor',
        singleClickEdit: true,
        valueGetter: (params :ValueGetterParams) => {
          return (
            params.data?.productOverride?.bufferQuantity ||
            params.data?.bufferQuantity ||
            ''
          );
        },
        valueSetter: (params :ValueSetterParams) => {
          const newValue = params.newValue;
          if (
            params.data.productOverride &&
            params.data.productOverride.bufferQuantity !== undefined
          ) {
            params.data.productOverride.bufferQuantity = newValue;
          } else {
            params.data.bufferQuantity = newValue;
          }

          return true;
        },
      },
      {
        headerName: 'Lead Time Days',
        field: EChannelPushStockOMSProduct.LEAD_TIME_DAYS,
        sortable: true,
        ..._flexWidth(100),
        editable: true,
        cellEditor: 'NumericEditor',
        singleClickEdit: true,
        valueGetter: (params :ValueGetterParams) => {
          return (
            params.data?.productOverride?.leadtimeDays ||
            params.data?.leadtimeDays ||
            ''
          );
        },
        valueSetter: (params :ValueSetterParams) => {
          const newValue = params.newValue;
          if (
            params.data.productOverride &&
            params.data.productOverride.leadtimeDays !== undefined
          ) {
            params.data.productOverride.leadtimeDays = newValue;
          } else {
            params.data.leadtimeDays = newValue;
          }

          return true;
        },
      },
      {
        hide: true,
        headerName: 'Inv Location',
        field: EChannelPushStockOMSProduct.INV_LOCATION,
        ..._flexWidth(150),
        editable: true,
        cellEditorParams: {
          channelId: channel?.id,
          mappingType: EProductMappingType.CHANNEL,
        },
        cellEditorFramework: ChannelProductMappingSearchInvLocationComponent,
        singleClickEdit: true,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        valueGetter: (params :ValueGetterParams) => {
          return (
            params.data?.productOverride?.inventoryLocation?.name ||
            params.data?.inventoryLocation?.name ||
            ''
          );
        },
        valueSetter: (params :ValueSetterParams) => {
          const newValue = params.newValue;
          if (params.data?.productOverride?.inventoryLocation) {
            params.data.productOverride.inventoryLocation.name = newValue?.name;
          } else {
            params.data.inventoryLocation = { name: newValue?.name };
          }

          return true;
        },
      },
      {
        hide: true,
        headerName: 'Product Status',
        field: EChannelPushStockOMSProduct.PRODUCT_STATUS,
        ..._flexWidth(150),
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 'active', label: 'ACTIVE' },
            { id: 'in-active', label: 'IN-ACTIVE' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return params.value?.toUpperCase() || '';
        },
      },
      {
        hide: true,
        headerName: 'Mapped To',
        field: EChannelPushStockOMSProduct.MAPPED_TO,
        ..._flexWidth(150),
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 'KIT', label: 'KIT' },
            { id: 'PRODUCT', label: 'PRODUCT' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return (
            params.data?.productOverride?.lumSku?.type ||
            params.data?.lumSku?.type ||
            ''
          );
        },
        valueGetter: (params :ValueGetterParams) => {
          return (
            params.data?.productOverride?.lumSku?.type ||
            params.data?.lumSku?.type ||
            ''
          );
        },
      },
      {
        headerName: 'Last Pushed',
        field: EChannelPushStockOMSProduct.LAST_PUSHED,
        ..._flexWidth(120),
        sortable: false,
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 'pushed', label: 'PUSHED' },
            { id: 'not_pushed', label: 'NOT PUSHED' },
          ],
        },
        cellRenderer: ChannelProductMappingLastPushedCellRendererComponent,
        valueGetter: (params :ValueGetterParams) => {
          const data = params.data;
          if (!data) return '';

          const lastPushQuantity = data?.productOverride?.lastPushQuantity || 0;
          const lastPushDatetime = data?.productOverride?.lastPushDatetime
            ? formatDate(data.productOverride.lastPushDatetime, 'short', 'en-US')
            : '';
          const remarks = data?.productOverride?.remarks || '';

          return `${lastPushDatetime} (${lastPushQuantity}) ${remarks}`;
        },
      },
      {
        headerName: 'Ignore',
        field: EChannelPushStockOMSProduct.IGNORED,
        ..._flexWidth(80),
        cellRenderer: ChannelProductMappingCheckboxCellRendererV2Component,
      },
      {
        headerName: 'Actions',
        field: 'actions',
        pinned: 'right',
        cellClass: 'no-border-highlight',
        cellRenderer: ChannelProductMappingActionCellRendererV2Component,
        cellRendererParams: buildParams.actionsCellParams,
        width: 160,
      },
    ],
    appIntegrationAccount: [
      {
        headerName: 'Product Name',
        field: EChannelPushStockOMSProduct.PRODUCT_NAME,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        flex: 1,
        sortable: true,
        minWidth: 200,
      },
      {
        headerName: 'SKU',
        field: EChannelPushStockOMSProduct.SKU,
        flex: 1,
        minWidth: 250,
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
      },
      {
        headerName: 'LUM SKU',
        field: EChannelPushStockOMSProduct.LUM_SKU,
        flex: 1,
        minWidth: 250,
        cellEditorFramework: ChannelProductMappingSearchSkuCellEditorComponent,
        cellEditorParams: {
          mappingType: EProductMappingType.APP_INTEGRATION_ACCOUNT,
        },
        singleClickEdit: true,
        editable: true,
        sortable: false,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        cellRenderer: ChannelProductMappingSkuCellRendererComponent,
        cellRendererParams: {
          mappingType: EProductMappingType.APP_INTEGRATION_ACCOUNT,
        },
      },
      {
        headerName: 'Selling Unit',
        field: EChannelPushStockOMSProduct.SELLING_UNIT,
        flex: 1,
        sortable: false,
        minWidth: 120,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value && params.node?.rowIndex) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        valueGetter: (params :any) => {
          if (params?.value) {
            return params.value || '';
          } else if (params.data?.lumSku) {
            return params.data?.lumSku?.defaultSellingUnit?.name || '';
          } else {
            return '';
          }
        },
      },
      {
        headerName: 'Default Stock Unit',
        field: EChannelPushStockOMSProduct.OMS_DEFAULT_UNIT,
        ..._flexWidth(150),
        cellEditorFramework: ChannelProductMappingSearchUomEditorComponent,
        singleClickEdit: true,
        editable: true,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value && params.node?.rowIndex) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        cellRenderer: (params :any) => {
          if (typeof params?.value === 'object') {
            return params?.value?.name || '';
          }
          const defaultUnit = params.data?.defaultUnitOfMeasure || null;

          if (defaultUnit) {
            return defaultUnit?.name || '';
          }

          return params?.value || '';
        },
      },

      {
        headerName: 'Stock Lvl [Pulled]',
        field: EChannelPushStockOMSProduct.STOCK_LEVEL_PULLED,
        sortable: false,
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 0, label: 'Out of Stock' },
            { id: 1, label: 'Has Stock' },
            { id: 100, label: 'Stocks lesser than 100' },
            { id: 500, label: 'Stocks lesser than 500' },
            { id: 1000, label: 'Stocks lesser than 1000' },
            { id: 3000, label: 'Stocks lesser than 3000' },
            { id: 3001, label: 'Stocks greater than 3000' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return `
          <div>
              ${params.data?.currentInventory || 0}
              ${
                params.data?.updatedAt
                  ? `<span class="text-muted"> [${formatDate(
                      params.data?.updatedAt,
                      'short',
                      'en-US',
                    )}]</span>`
                  : ''
              }
          </div>`;
        },
      },
      {
        headerName: 'Price',
        field: EChannelPushStockOMSProduct.PRICE,
        sortable: true,
      },

      {
        headerName: 'Qty Available',
        field: EChannelPushStockOMSProduct.QTY_AVAILABLE,
        sortable: false,
        cellRenderer: (params :ICellRendererParams) => {
          if (params.data?.lumSku) {
            const { locationStocks } = params.data?.lumSku;
            const qtyAvailable =
              locationStocks && locationStocks.length > 0
                ? decimalPlacesPipe.transform(
                    locationStocks.reduce((sum, stock) => {
                      const qtyOnHand = Number(stock.qty_onhand) || 0;
                      const qtyPending = Number(stock.qty_pending) || 0;

                      return sum + (qtyOnHand - qtyPending);
                    }, 0),
                  )
                : '';

            return qtyAvailable;
          }

          return '';
        },
      },
      {
        headerName: 'Buffer Quantity',
        field: EChannelPushStockOMSProduct.BUFFER_QUANTITY,
        sortable: true,
        minWidth: 150,
        editable: true,
        cellEditor: 'NumericEditor',
        singleClickEdit: true,
        cellRenderer: (params :ICellRendererParams) => {
          return params?.value || params.data?.lumSku?.bufferQuantity || '';
        },
      },
      {
        headerName: 'Lead Time Days',
        field: EChannelPushStockOMSProduct.LEAD_TIME_DAYS,
        sortable: true,
        minWidth: 150,
        editable: true,
        cellEditor: 'NumericEditor',
        singleClickEdit: true,
        cellRenderer: (params :ICellRendererParams) => {
          return params?.value || params.data?.lumSku?.leadtimeDays || '';
        },
      },
      {
        headerName: 'Inv Location',
        field: EChannelPushStockOMSProduct.INV_LOCATION,
        editable: true,
        cellEditorParams: {
          channelId: channel?.id,
          mappingType: EProductMappingType.APP_INTEGRATION_ACCOUNT,
        },
        cellEditorFramework: ChannelProductMappingSearchInvLocationComponent,
        singleClickEdit: true,
        minWidth: 150,
        onCellClicked: (params :CellClickedEvent) => {
          if (params?.value) {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex,
              colKey: params.column.getColId(),
            });
          }
        },
        valueGetter: (params :ValueGetterParams) => {
          return params.data?.inventoryLocation?.name || '';
        },
        valueSetter: (params :ValueSetterParams) => {
          const newValue = params.newValue;
          params.data.inventoryLocation = { name: newValue?.name };

          return true;
        },
      },
      {
        headerName: 'Product Status',
        field: EChannelPushStockOMSProduct.PRODUCT_STATUS,
        minWidth: 150,
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 'active', label: 'ACTIVE' },
            { id: 'in-active', label: 'IN-ACTIVE' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return params.value?.toUpperCase() || '';
        },
      },
      {
        headerName: 'Mapped To',
        field: EChannelPushStockOMSProduct.MAPPED_TO,
        minWidth: 150,
        filter: 'AgGridCustomRadioFilterComponent',
        floatingFilter: true,
        filterParams: {
          debounceMs: 1000,
          values: [
            { id: null, label: 'ALL', isDefault: true },
            { id: 'KIT', label: 'KIT' },
            { id: 'PRODUCT', label: 'PRODUCT' },
          ],
        },
        cellRenderer: (params :ICellRendererParams) => {
          return params.data?.lumSku?.type || '';
        },
      },
      {
        headerName: 'Ignore',
        field: EChannelPushStockOMSProduct.IGNORED,
        ..._flexWidth(80),
        cellRenderer: AppIntegrationAccountProductMappingCheckboxCellRendererComponent,
      },
    ],
  };
};

const relations = {
  channel: [
    'lumSku',
    'lumSku.defaultSellingUnit',
    'lumSku.locationStocks',
    'appIntegrationAccount',
    'appIntegrationAccount.pushStocksChannelIntegrationMapping',
    'appIntegrationAccount.pushStocksChannelIntegrationMapping.channel',
    'productOverride',
    'productOverride.sellingUnit',
    'productOverride.lumSku.defaultSellingUnit',
    'productOverride.lumSku.locationStocks',
    'inventoryLocation',
    'productOverride.inventoryLocation',
  ],
  appIntegrationAccount: [
    'lumSku',
    'lumSku.defaultSellingUnit',
    'defaultUnitOfMeasure',
    'lumSku.locationStocks',
    'inventoryLocation',
  ],
};

export const ChannelAgGridConfig = {
  buildProductMappingAgGridColDefs,
  relations,
};
